/*
 * Copyright (c) 2024-2026 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import {Component, effect, HostListener, OnInit} from '@angular/core';
import {EditableGridComponent} from '../editable-grid-component';
import {CellEditingStoppedEvent, ColDef} from 'ag-grid-community';
import {ActionsCellRendererComponent} from '../cell-renderer/actions-cell-renderer.component';
import {MomentumProgram} from '../../model/momentumProgram.model';
import {ProgramRun} from '../../model/programRun';
import {GpsTiming} from '../../model/gpsProgramTiming.model';
import {Program} from '../../model/program';
import {ValidationModel} from '../../model/validation.model';
import {refreshCellAndDeferredRowRedraw} from '../cell-renderer/prime-cell-editor-tab.util';

@Component({
    selector: 'app-icd-program-timing',
    templateUrl: './icd-program-timing.component.html',
    styleUrls: ['./icd-program-timing.component.scss'],
    standalone: false
})
export class IcdProgramTimingComponent extends EditableGridComponent implements OnInit {
  permissionUrl = '/esciUI/icdProgramTimingViewPermission';
  adminPermissionUrl = '/esciUI/icdProgramTimingAdminPermission';
  saveUrl = '/esciUI/saveMomentumProgramsForIcdTiming';
  updateIcdsUrl = '/esciUI/updateIcdDatesBasedOnSavedTiming';
  rowData: MomentumProgram[] = [];
  adminOnlyColumns = ['actions'];
  selectedRowCount = 0;
  programs = new Map<string, Program>();
  programRuns = new Map<string, ProgramRun>();
  gpsTimingFromDB = [];
  gpsTiming = new Map<string, GpsTiming>();
  modelYears = [];
  dropdownsInitialized = false;
  cols: ColDef[] = [
    { headerCheckboxSelection: true, checkboxSelection: true, minWidth: 130, maxWidth: 130, sortable: false,
      headerName: 'Actions', colId: 'actions', editable: false, filter: false, lockPosition: true, suppressMovable: true,
      cellRenderer: ActionsCellRendererComponent,
      cellRendererParams: {
        onDeleteClick: (function (params: any): void {
          this.onDeleteClick(params, this.rowData, (updatedData) => this.refreshGrid(updatedData));
        }).bind(this),
        onCopyClick: this.onCopyClick.bind(this),
        fieldForDeleteMessage: 'program.programName'
      }
    },
    {field: 'program.programName', headerName: 'eSCI Program Name'},
    {field: 'modelYear', headerName: 'eSCI Model Year'},
    {field: 'programRun.programName', headerName: 'PROD Program Run'},
    {field: 'gpsTiming.labelForDropdown', headerName: 'GPS Timing Program', minWidth: 280, maxWidth: 280},
    {field: 'gpsTiming.mrd', headerName: 'GPS Timing MRD', editable: false, cellEditor: 'agDateCellEditor', cellDataType: 'date'},
    {field: 'icdUpdateDate', headerName: 'Date ICDs Were Last Updated', editable: false, cellEditor: 'agDateCellEditor', cellDataType: 'date'}
  ];

  ngOnInit(): void {
    this.cols.forEach(col => {
      col.tooltipValueGetter = (params) => {
        return params.data.changes.get(col.field);
      }
    });
      // React when permission signal changes (instead of checking once too early)
      effect(() => {
          if (!this.dropdownsInitialized && this.adminPermission()) {
              this.dropdownsInitialized = true;
              this.setupDropdowns();
          }
      }, { injector: this.injector });
      this.permission.set(false);
      this.esciService.getPermissionAndFileSizeLimit(this, false);
      this.esciService.getMomentumProgramsForIcdTiming(this, () => {
          this.setColumnsVisible();
          this.reset();
      });
  }

  setupDropdowns() {
    // set modelyears to 2020-2050
    this.modelYears.push(...Array.from({length: 31}, (v, k) => (2020 + k).toString()));
    this.setDropdownOption(this.modelYears, 'modelYear');
    // get all icd programs, program runs, and gps timing
    let programs = [];
    this.setDropdownOption(programs, 'program.programName');
    this.esciService.getIcdProgramsForIcdTiming(this, () => {
      programs.push(...this.programs.keys());
    });
    let programRuns = [];
    this.setDropdownOption(programRuns, 'programRun.programName');
    this.esciService.getProgramRunsForIcdTiming(this, () => {
      // Backend now filters to PROD and omits heavy timing row payload for this dropdown call.
      this.programRuns.set('', new ProgramRun());
      programRuns.push(...this.programRuns.keys());
    });
    let gpsTimings = [];
    this.setDropdownOption(gpsTimings, 'gpsTiming.labelForDropdown');
    this.esciService.getGpsTiming(this, () => {
      this.gpsTimingFromDB.forEach(gps => {
        if (gps.mrdType?.toUpperCase() !== 'PPV') {
          this.gpsTiming.set(gps.labelForDropdown, gps);
        }
      });
      this.gpsTiming.set('', new GpsTiming());
      gpsTimings.push(...this.gpsTiming.keys());
    });
  }

  saveMomentumPrograms() {
    if (this.areRowsValid(this.rowData) && this.adminPermission()) {
      // custom validation for required fields that are either/or
      this.rowData.forEach(row => {
        if (!row.programRun?.programName && !row.gpsTiming?.labelForDropdown) {
          this.rowsInvalid = true;
          this.messageService.processError('Validation failure - Either Program Run Name (PROD) or GPS Timing Program is required. Please correct the field and try again.');
        } else if (row.programRun?.programName && row.gpsTiming?.labelForDropdown) {
          this.rowsInvalid = true;
          this.messageService.processError('Validation failure - Rows cannot have both a Program Run Name (PROD) and GPS Timing Program. Please correct the row and try again.');
        }
      });
      if (this.rowsInvalid) {
        return;
      }

      this.rowData.forEach(row => {
        row['MODEL_YEAR'] = row.modelYear;
      })
      this.saving = true;
      this.dataChanged = false;
      // remove any rows where state is deleted
      this.setRowData(this.rowData.filter(row => row.state !== 'deleted'));
      this.esciService.saveRows(this, this.rowData, 'ICD Program Timing',
          () => this.esciService.getMomentumProgramsForIcdTiming(this, () => {
            this.setColumnsVisible();
            this.reset();
          })
      );
    }
  }

  exportReport() {
    // export all data in the grid
    this.excelService.exportGridDataAsExcelFile(this.gridApi, 'ICD_Program_Timing');
  }

  onCopyClick(params): void {
    // find the index of the row clicked on
    const index = this.rowData.findIndex(row => row.id === params.data.id);
    // copy the row and add it to the array at the index
    const newRow = structuredClone(params.data);
    newRow.originalRowData = structuredClone(newRow);
    newRow.changes.clear();
    newRow.id = -1;
    newRow.icdUpdateDate = null;
    newRow.state = 'new';
    this.rowData.splice(index + 1, 0, newRow);
    this.dataChanged = true;
    // update the grid
    this.refreshGrid(this.rowData);
  }

  resetChanges() {
    this.resetting = true;
    this.exporting = true;
    this.messageService.clear();
    this.esciService.getMomentumProgramsForIcdTiming(this, () => this.reset());
  }

  reset() {
    super.reset();
    this.refreshGrid(this.rowData);
    this.onSelectionChanged();
  }
  @HostListener('window:resize', ['$event'])
  onResize(event: any) {
    if (this.gridApi && !this.gridApi.isDestroyed()) {
      this.gridApi.sizeColumnsToFit();
    }
  }

  onSelectionChanged() {
    this.selectedRowCount = this.gridApi?.getSelectedRows()?.length ?? 0;
  }

  updateIcdDates() {
    if (!this.dataChanged) {
      const rowsToUse = this.gridApi.getSelectedRows();
      rowsToUse.forEach(row => {
        row['MODEL_YEAR'] = row.modelYear;
      });
      this.esciService.updateIcdDates(this, rowsToUse, () => {
        this.esciService.getMomentumProgramsForIcdTiming(this, () => {
          this.setColumnsVisible();
          this.reset();
        });
      });
    }
  }

  addRow() {
    // add a row to the grid
    const newRow = new MomentumProgram();
    newRow.originalRowData = structuredClone(newRow);
    newRow.changes.clear();
    newRow.id = -1;
    newRow.icdUpdateDate = null;
    newRow.state = 'new';
    this.rowData.splice(0, 0, newRow);
    this.dataChanged = true;
    this.refreshGrid(this.rowData);
  }

  // when user stops editing a cell, automatically validate and highlight modified rows
  cellEditingStopped(event: CellEditingStoppedEvent<any>, rows: ValidationModel[]) {
    if (event.value === event.oldValue) {
      return;
    }
    // set the actual objects after dropdown has been selected
    if (event.column.getColId() === 'gpsTiming.labelForDropdown') {
      event.data.gpsTiming = structuredClone(this.gpsTiming.get(event.value));
    }
    if (event.column.getColId() === 'program.programName') {
        event.data.program = structuredClone(this.programs.get(event.value));
    }
    if (event.column.getColId() === 'programRun.programName') {
        event.data.programRun = structuredClone(this.programRuns.get(event.value));
    }
    // Immediately recompute row state so changed styling appears without another interaction.
    this.updateDataChanged(rows);
    if (event.node && this.gridApi && !this.gridApi.isDestroyed() && event.column) {
      refreshCellAndDeferredRowRedraw(this.gridApi, event.node, event.column);
    }
    if (event.data.state !== 'new' || this.rowsInvalid || this.duplicate) {
      // it is annoying when user gets error messages as they are filling out a new row
      // validation will still happen on save
      this.areRowsValid(rows);
    }
    // Avoid refreshGrid on cell stop — full rowData replace clears ag-grid focus (Tab navigation).
  }
}
